import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/utils/cn';

type Placement = 'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top-end' | 'top';

interface PopoverProps {
  trigger: ReactElement<{ onClick?: (e: MouseEvent<HTMLElement>) => void; ref?: React.Ref<HTMLElement> }>;
  children: ReactNode | ((ctx: { close: () => void }) => ReactNode);
  placement?: Placement;
  offset?: number;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  closeOnClickInside?: boolean;
  matchTriggerWidth?: boolean;
}

export function Popover({
  trigger,
  children,
  placement = 'bottom-start',
  offset = 6,
  className,
  open: controlledOpen,
  onOpenChange,
  closeOnClickInside = true,
  matchTriggerWidth,
}: PopoverProps) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolled;

  const triggerRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties>({});

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolled(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  // Stable ref callback so it isn't recreated each render (react-hooks/refs).
  const setTriggerRef = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node;
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const t = triggerRef.current;
    const p = popoverRef.current;
    if (!t || !p) return;

    const compute = () => {
      const tRect = t.getBoundingClientRect();
      const pRect = p.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let top = 0;
      let left = 0;

      switch (placement) {
        case 'bottom-start':
          top = tRect.bottom + offset;
          left = tRect.left;
          break;
        case 'bottom-end':
          top = tRect.bottom + offset;
          left = tRect.right - pRect.width;
          break;
        case 'bottom':
          top = tRect.bottom + offset;
          left = tRect.left + tRect.width / 2 - pRect.width / 2;
          break;
        case 'top-start':
          top = tRect.top - pRect.height - offset;
          left = tRect.left;
          break;
        case 'top-end':
          top = tRect.top - pRect.height - offset;
          left = tRect.right - pRect.width;
          break;
        case 'top':
          top = tRect.top - pRect.height - offset;
          left = tRect.left + tRect.width / 2 - pRect.width / 2;
          break;
      }

      // Clamp into viewport
      left = Math.max(8, Math.min(left, vw - pRect.width - 8));
      top = Math.max(8, Math.min(top, vh - pRect.height - 8));

      const style: CSSProperties = { top, left };
      if (matchTriggerWidth) style.minWidth = tRect.width;
      setPos(style);
    };

    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, placement, offset, matchTriggerWidth]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!isValidElement(trigger)) {
    throw new Error('Popover trigger must be a valid React element');
  }

  // The ref callback only runs at commit, not render — the rule can't see that
  // through cloneElement, so suppress its false positive here.
  // eslint-disable-next-line react-hooks/refs
  const triggerElement = cloneElement(trigger, {
    ref: setTriggerRef,
    onClick: (e: MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(e);
      setOpen(!open);
    },
  });

  return (
    <>
      {triggerElement}
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className={cn('ds-popover', className)}
            style={pos}
            onClick={(e) => {
              if (closeOnClickInside) {
                const target = e.target as HTMLElement;
                if (target.closest('.ds-menu-item')) close();
              }
            }}
          >
            {typeof children === 'function' ? children({ close }) : children}
          </div>,
          document.body,
        )}
    </>
  );
}

interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  danger?: boolean;
}

export function MenuItem({ icon, danger, className, children, ...rest }: MenuItemProps) {
  return (
    <button
      type="button"
      className={cn('ds-menu-item', danger && 'danger', className)}
      {...rest}
    >
      {icon}
      <span style={{ flex: 1 }}>{children}</span>
    </button>
  );
}

export function MenuDivider() {
  return <div className="ds-menu-divider" />;
}

export function MenuSectionLabel({ children }: { children: ReactNode }) {
  return <div className="ds-menu-section-label">{children}</div>;
}
